import { useEffect, useRef, useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import {
  continueRender,
  delayRender,
  useCurrentFrame,
  useVideoConfig,
} from 'remotion';

// Components
import { confettiCannon } from './Confetti';

// Types
import type { ConfettiConfig } from './types';

interface ConfettiProps extends ConfettiConfig {
  style?: CSSProperties;
  fireAtFrame?: number;
}

export const Confetti = ({
  style,
  fireAtFrame = 0,
  ...confettiConfig
}: ConfettiProps) => {
  const frame = useCurrentFrame();
  const { width, height } = useVideoConfig();

  const [handle] = useState(() => delayRender('Initializing confetti'));
  const [isReady, setIsReady] = useState(false);

  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Only create confetti instance after fireAtFrame
  const shouldRender = frame >= fireAtFrame;

  const confettiInstance = useMemo(() => {
    if (!isReady || !canvasRef.current || !shouldRender) return null;

    const cannon = confettiCannon(canvasRef.current);
    cannon.fire({ ...confettiConfig, width, height });

    return cannon;
  }, [isReady, shouldRender, confettiConfig, width, height]);

  useEffect(() => {
    if (!confettiInstance) return;

    // Use relative frame starting from 1 (not 0) to show initial movement
    const relativeFrame = frame - fireAtFrame + 1;
    confettiInstance.frame(relativeFrame);
  }, [confettiInstance, frame, fireAtFrame]);

  useEffect(() => {
    setIsReady(true);
    continueRender(handle);
  }, [handle]);

  const canvasStyle = useMemo<CSSProperties>(
    () => ({
      width,
      height,
      position: 'absolute',
      ...style,
    }),
    [width, height, style],
  );

  // Don't render canvas until fireAtFrame is reached
  if (!shouldRender) return null;

  return (
    <canvas ref={canvasRef} width={width} height={height} style={canvasStyle} />
  );
};

export default Confetti;
